import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_ODOO_BASE_URL; // Base API URL
const BEARER_TOKEN = process.env.REACT_APP_ODOO_TOKEN; // Bearer token

// API Request Headers
const headers = {
  Authorization: `Bearer ${BEARER_TOKEN}`,
  "Content-Type": "application/json",
};

export enum InvoiceType {
  Logistic = "logistic",
  Penalty = "penalty",
  All = ""
}

// Map journal IDs to invoice types
export const journalToInvoiceType = (journalId: number): InvoiceType => {
  switch (journalId) {
    case 76:
      return InvoiceType.Logistic;
    case 74:
      return InvoiceType.Penalty;
    default:
      return InvoiceType.Logistic; // Default to logistic if unknown
  }
};

// Interface for invoice item
export interface InvoiceItem {
  ref: string;
  date: string;
  journal_id: number;
  total: number;
  type?: InvoiceType; // Derived from journal_id
  status?: string;
  [key: string]: any; // For additional fields that might be in the response
}

// Interface for invoice response
export interface InvoiceResponse {
  jsonrpc: string;
  id: null;
  result: {
    status: string;
    total_invoices_amount: number;
    invoices: InvoiceItem[];
    pagination: {
      page: number;
      page_size: number;
      total_records: number;
      total_pages: number;
    };
  };
}

/**
 * Invoice KPI response interface
 */
export interface InvoiceKPIResponse {
  jsonrpc: string;
  id: null;
  result: {
    status: string;
    logistic_total_records: number;
    logistic_invoices_amount: number;
    penalty_total_records: number;
    penalty_invoices_amount: number;
  };
}

/**
/**
 * Invoice Line item in details response
 */
export interface InvoiceLineItem {
  label: string;
  amount: number;
  [key: string]: any;
}

/**
 * Invoice details response
 */
export interface InvoiceDetailsResponse {
  jsonrpc: string;
  id: null;
  result: {
    status: string;
    details: {
      ref: string;
      date: string;
      invoice_lines: InvoiceLineItem[];
      total: number;
      [key: string]: any;
    };
  };
}

/**
 * Gets invoices for a seller based on filters
 * @param sellerId - The seller ID 
 * @param type - The invoice type (logistic or penalty)
 * @param fromDate - Start date for filtering (optional)
 * @param toDate - End date for filtering (optional)
 * @param page - Page number for pagination
 * @param pageSize - Number of records per page
 * @returns Promise with the invoice data
 */
export const getInvoicesBySeller = async (
  sellerId: string,
  type: InvoiceType | string = "",
  fromDate: string = "",
  toDate: string = "",
  page: string | number = 1,
  pageSize: string | number = 10
): Promise<InvoiceResponse> => {
  try {
    // Log what's being sent to the API
    console.log("Type filter being sent to API:", type);
    
    const response = await axios.post(
      `${BASE_URL}/seller/fee`,
      {
        seller_id: sellerId,
        type: type, // This should be "logistic" or "penalty" or ""
        from: fromDate,
        to: toDate,
        page: page.toString(),
        page_size: pageSize.toString()
      },
      { headers }
    );
    
    // Add type property based on journal_id
    if (response.data?.result?.invoices) {
      response.data.result.invoices = response.data.result.invoices.map((invoice: InvoiceItem) => ({
        ...invoice,
        type: journalToInvoiceType(invoice.journal_id)
      }));
    }
    
    return response.data;
  } catch (error: any) {
    console.error("Error fetching invoices:", error);
    toast.error(
      error.response?.data?.error?.message || 
      "Failed to fetch invoices. Please try again later."
    );
    throw error;
  }
};

/**
 * Gets KPI data for invoices
 * @param sellerId - The seller ID
 * @param fromDate - Start date for filtering (optional)
 * @param toDate - End date for filtering (optional)
 * @returns Promise with the KPI data
 */
export const getInvoiceKPIs = async (
  sellerId: string,
  fromDate: string = "",
  toDate: string = ""
): Promise<InvoiceKPIResponse> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/seller/fee/kpi`,
      {
        seller_id: sellerId,
        from: fromDate,
        to: toDate
      },
      { headers }
    );
    
    return response.data;
  } catch (error: any) {
    console.error("Error fetching invoice KPIs:", error);
    toast.error(
      error.response?.data?.error?.message || 
      "Failed to fetch invoice statistics. Please try again later."
    );
    throw error;
  }
};

/**
 * Gets invoice details by reference
 * @param ref - The invoice reference
 * @param type - The invoice type (optional)
 * @param journalId - The journal ID (optional)
 * @returns Promise with the invoice details
 */
export const getInvoiceDetailsByRef = async (
  ref: string,
  type?: InvoiceType | string,
  journalId?: number
): Promise<InvoiceDetailsResponse> => {
  try {
    // Determine which endpoint to use based on type or journal ID
    let endpoint = `${BASE_URL}/logistic/fee/ref`; // Default to logistic
    
    if (type === InvoiceType.Penalty || journalId === 74) {
      endpoint = `${BASE_URL}/sellerpenalty/fee/ref`;
      console.log("Using penalty API endpoint for invoice", ref);
    } else if (type === InvoiceType.Logistic || journalId === 76) {
      endpoint = `${BASE_URL}/logistic/fee/ref`;
      console.log("Using logistic API endpoint for invoice", ref);
    }
    
    const response = await axios.post(
      endpoint,
      {
        ref: ref
      },
      { headers }
    );
    
    return response.data;
  } catch (error: any) {
    console.error("Error fetching invoice details:", error);
    toast.error(
      error.response?.data?.error?.message || 
      "Failed to fetch invoice details. Please try again later."
    );
    throw error;
  }
};
